import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';

const AdminFundTransfer = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [password, setPassword] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [transactionReference, setTransactionReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historySponsorSearch, setHistorySponsorSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Initial lightweight fetch for user selection
  useEffect(() => {
    fetchTransferUsers('');
  }, []);

  // Server-side search for users
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchTransferUsers(searchTerm);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTransferUsers = async (search = '') => {
    try {
      const response = await adminAPI.getTransferUsers({ search, limit: 50 });
      setUsers(response?.data?.users || []);
    } catch (err) {
      console.error('Failed to fetch transfer users:', err);
      setError('Failed to load users');
    }
  };

  const fetchWalletTransactions = async () => {
    try {
      const response = await adminAPI.getWalletTransactions();
      setWalletTransactions(response.data.data || []);
      setShowHistory(true);
    } catch (err) {
      console.error('Failed to fetch wallet transactions:', err);
      setError('Failed to load wallet transactions');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedUser || !amount || parseFloat(amount) <= 0) {
      setError('Please select a user and enter a valid amount');
      return;
    }

    setLoading(true);

    try {
      const response = await adminAPI.transferFunds({
        recipientUserId: selectedUser,
        amount: parseFloat(amount),
        description: description || 'Physical payment received',
        paymentMethod: paymentMethod,
        transactionReference: transactionReference || `ADMIN-${Date.now()}`,
        password
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        // Reset form
        setSelectedUser('');
        setAmount('');
        setDescription('');
        setPassword('');
        setPaymentMethod('CASH');
        setTransactionReference('');
        setSearchTerm('');
        // Refresh wallet transactions if showing
        if (showHistory) {
          fetchWalletTransactions();
        }
      } else {
        setError(response.data.error || 'Transfer failed');
      }
    } catch (err) {
      console.error('Transfer error:', err);
      setError(err.response?.data?.error || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId) => {
    setSelectedUser(userId);
    setShowDropdown(false);
    // Clear search term after selection
    const selectedUserDetails = users.find(user => user._id === userId);
    if (selectedUserDetails) {
      setSearchTerm(selectedUserDetails.mySponsorCode);
    }
  };

  const selectedUserDetails = users.find(user => user._id === selectedUser);
  const filteredWalletTransactions = walletTransactions.filter((transaction) => {
    const normalizedSearch = historySponsorSearch.trim().toLowerCase();
    if (!normalizedSearch) return true;
    return (transaction.userSponsorCode || '').toLowerCase().includes(normalizedSearch);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-[#0F2F3D] mb-2">Wallet Top-up</h2>
        <p className="text-gray-600">Add funds to user wallet after receiving physical payment</p>
      </div>

      {/* Transfer Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User Selection with Search */}
          <div className="relative">
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
              Select Recipient User (Search by Sponsor Code)
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowDropdown(true);
                  if (selectedUser) {
                    setSelectedUser(''); // Clear selected user when searching
                  }
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search by sponsor code..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all"
              />
              {showDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {users.length > 0 ? (
                    users.map((user) => (
                      <div
                        key={user._id}
                        onClick={() => handleUserSelect(user._id)}
                        className="px-4 py-3 hover:bg-orange-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-gray-900">{user.name}</span>
                            <span className="text-sm text-gray-500 ml-2">({user.email})</span>
                          </div>
                          <span className="text-sm font-mono bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {user.mySponsorCode}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-gray-500 text-center">
                      No users found with this sponsor code
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Selected User Details */}
          {selectedUserDetails && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Selected User Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Name:</span>
                  <p className="text-gray-900">{selectedUserDetails.name}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Email:</span>
                  <p className="text-gray-900">{selectedUserDetails.email}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">My Sponsor ID:</span>
                  <p className="text-gray-900 font-mono">{selectedUserDetails.mySponsorCode}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Sponsor Code Used:</span>
                  <p className="text-gray-900 font-mono">{selectedUserDetails.sponsorCodeUsed || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Sponsor Name:</span>
                  <p className="text-gray-900">{selectedUserDetails.sponsorName || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Current Wallet:</span>
                  <p className="text-green-600 font-semibold">
                    ₹{(selectedUserDetails.wallet?.depositWallet || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Total Purchase:</span>
                  <p className="text-orange-600 font-semibold">
                    ₹{(selectedUserDetails.wallet?.totalPayout || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Rank:</span>
                  <p className="text-gray-900">{selectedUserDetails.rank || 'Not Ranked'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
              Amount (₹)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount to transfer"
              min="1"
              step="0.01"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all"
              required
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
              Payment Method
            </label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all"
            >
              <option value="CASH">Cash</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="UPI">UPI</option>
              <option value="CHEQUE">Cheque</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          {/* Password */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all"
              required
            />
          </div>

          {/* Transaction Reference */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
              Transaction Reference (Optional)
            </label>
            <input
              type="text"
              value={transactionReference}
              onChange={(e) => setTransactionReference(e.target.value)}
              placeholder="Enter transaction reference or receipt number"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description for this transfer"
              rows="3"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all resize-none"
            />
          </div>

          {/* Error and Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !selectedUser || !amount || !password}
            className="w-full bg-[#E4572E] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#c73e1a] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing Top-up...' : 'Add Funds to Wallet'}
          </button>
        </form>
      </div>

      {/* Wallet Transactions Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-[#0F2F3D]">Fund Transfer History</h3>
          <button
            onClick={() => showHistory ? setShowHistory(false) : fetchWalletTransactions()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
          >
            {showHistory ? 'Hide History' : 'Show History'}
          </button>
        </div>

        {showHistory && (
          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-sm font-semibold text-[#0F2F3D]">
                Search by Sponsor Code
              </label>
              <input
                type="text"
                value={historySponsorSearch}
                onChange={(e) => setHistorySponsorSearch(e.target.value)}
                placeholder="Enter sponsor code..."
                className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E4572E] focus:ring-2 focus:ring-[#E4572E]/20 transition-all"
              />
            </div>

            <div className="overflow-x-auto">
            {filteredWalletTransactions.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account
                    </th> */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th> */}
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction Reference
                    </th> */}
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th> */}
                    {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th> */}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredWalletTransactions.map((transaction, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(transaction.processedAt || transaction.requestedAt).toLocaleString('en-GB')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.userName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {transaction.userEmail}
                        </div>
                        <div className="text-xs text-blue-600 font-mono">
                          {transaction.userSponsorCode}
                        </div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {transaction.accountHolder || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-600 font-mono">
                          {transaction.accountNumber ? `A/C: ${transaction.accountNumber}` : 'Account not added'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {transaction.bankName || ''}
                        </div>
                      </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                        ₹{transaction.amount.toLocaleString()}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {transaction.paymentMethod}
                        </span>
                      </td> */}
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.transactionReference}
                      </td> */}
                      {/* <td className="px-6 py-4 text-sm text-gray-900">
                        {transaction.remarks}
                      </td> */}
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          {transaction.status}
                        </span>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {walletTransactions.length > 0
                  ? 'No fund transfer history found for this sponsor code'
                  : 'No fund transfer history found'}
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFundTransfer;